import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";

import DispatchHeader from "@/components/DispatchHeader";
import RosterSidebar from "@/components/RosterSidebar";
import ChannelGrid from "@/components/ChannelGrid";
import LiveMap from "@/components/LiveMap";
import EmergencyAlerts from "@/components/EmergencyAlerts";
import AudioLogger from "@/components/AudioLogger";
import BroadcastBar from "@/components/BroadcastBar";
import DashboardGrid, { DEFAULT_LAYOUT } from "@/components/DashboardGrid";
import LoginPage from "@/pages/LoginPage";

import { IDS } from "@/constants/testIds";
import { usePTT } from "@/hooks/usePTT";
import { useBrowserSpeech } from "@/hooks/useBrowserSpeech";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { TONES, playTone, renderToneWav, toneDurationMs } from "@/lib/tones";
import {
  listChannels, createChannel, updateChannel, deleteChannel,
  listContacts, createContact, deleteContact, toggleContactOnline,
  listTransmissions, createTransmission, createBroadcast,
  listAlerts, createAlert, ackAlert, deleteAlert,
  listPatches, createPatch, togglePatch, deletePatch,
  listTalkgroups, createTalkgroup, addTalkgroupMembers, removeTalkgroupMember, deleteTalkgroup,
  getSettings, saveSettings, formatApiError,
} from "@/lib/api";

const DEFAULT_SETTINGS = { provider: "mock", dispatcher_name: "Dispatcher", map_provider: "osm", google_maps_key: "", unit_movement: true, sos_sim_rate: 0.01, sos_audible: true, mic_device_id: "", speaker_device_id: "", timezone: "UTC", archive_retention_minutes: 0, theme: "dark", transcription: "off", show_trails: true, layout: null, hidden_panes: [] };

function Console() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [transmissions, setTransmissions] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [patches, setPatches] = useState([]);
  const [talkgroups, setTalkgroups] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [muted, setMuted] = useState(false);
  const [layoutLocked, setLayoutLocked] = useState(true);

  // Group broadcast multi-select
  const [selectMode, setSelectMode] = useState(false);
  const [selChannels, setSelChannels] = useState([]);
  const [selContacts, setSelContacts] = useState([]);
  const [mapSelected, setMapSelected] = useState(null);

  // Active PTT tracking (channel | direct | broadcast — one at a time)
  const [activeTarget, setActiveTarget] = useState(null);
  const ptt = usePTT();
  const speech = useBrowserSpeech();
  const isTransmitting = ptt.recording;
  const isStarting = useRef(false);

  const refreshAll = useCallback(async () => {
    const [c, u, t, a, p, s, g] = await Promise.all([
      listChannels(), listContacts(), listTransmissions({ limit: 100 }), listAlerts(), listPatches(), getSettings(), listTalkgroups(),
    ]);
    setChannels(c); setContacts(u); setTransmissions(t); setAlerts(a); setPatches(p); setSettings(s); setTalkgroups(g);
  }, []);

  useEffect(() => { refreshAll().catch(e => toast.error(formatApiError(e))); }, [refreshAll, user?.id]);

  // Theme
  useEffect(() => {
    const root = document.documentElement;
    const apply = () => {
      const dark = settings.theme === "dark" || (settings.theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
      root.classList.toggle("dark", dark);
    };
    apply();
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [settings.theme]);

  // Poll for live traffic + unit movement
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const [t1, a1, c1] = await Promise.all([listTransmissions({ limit: 100 }), listAlerts(), listContacts()]);
        setTransmissions(t1); setAlerts(a1); setContacts(c1);
      } catch {}
    }, 3500);
    return () => clearInterval(t);
  }, []);

  // Audible SOS: siren tone when a fresh high/critical alert arrives (sim, test, or real)
  const seenAlertIds = useRef(new Set());
  useEffect(() => {
    if (seenAlertIds.current.size > 500) seenAlertIds.current = new Set(alerts.map(a => a.id));
    const now = Date.now();
    for (const a of alerts) {
      if (seenAlertIds.current.has(a.id)) continue;
      seenAlertIds.current.add(a.id);
      if (!settings.sos_audible || a.acknowledged) continue;
      if (a.severity !== "high" && a.severity !== "critical") continue;
      const ageMs = now - new Date(a.created_at).getTime();
      if (!Number.isFinite(ageMs) || ageMs > 120000) continue; // only sound for fresh alerts
      try { playTone("hi_lo", 1, 0.9); } catch {}
      toast.warning(`SOS — ${a.contact_name}: ${a.message}`, { duration: 6000 });
    }
  }, [alerts, settings.sos_audible]);

  // === PTT flow ===
  const startPTT = useCallback(async (target) => {
    if (isStarting.current || activeTarget) return;
    isStarting.current = true;
    const ok = await ptt.start(settings.mic_device_id);
    isStarting.current = false;
    if (!ok) {
      toast.error(ptt.error || "Microphone permission denied");
      return;
    }
    if (settings.transcription === "browser") speech.start();
    setActiveTarget(target);
    toast(`Transmitting on ${target.name}`, { duration: 1200 });
  }, [ptt, speech, activeTarget, settings.mic_device_id, settings.transcription]);

  const stopPTT = useCallback(async () => {
    if (!activeTarget) return;
    const target = activeTarget;
    setActiveTarget(null);
    const [result, transcript] = await Promise.all([ptt.stop(), settings.transcription === "browser" ? speech.stop() : Promise.resolve(null)]);
    if (!result) return;
    if (result.durationMs < 250) {
      toast.info("Transmission too short — discarded");
      return;
    }
    const sender = settings.dispatcher_name || user?.name || "Dispatcher";
    const common = { sender_name: sender, duration_ms: result.durationMs, mime_type: result.mimeType, audio_b64: result.base64, transcript: transcript || null };
    try {
      if (target.type === "broadcast") {
        const legs = await createBroadcast({ channel_ids: target.channelIds, contact_ids: target.contactIds, ...common });
        toast.success(`Broadcast ${(result.durationMs/1000).toFixed(1)}s to ${legs.length} targets`);
      } else {
        await createTransmission({
          channel_id: target.type === "channel" ? target.id : null,
          contact_id: target.type === "direct" ? target.id : null,
          talkgroup_id: target.type === "talkgroup" ? target.id : null,
          kind: target.type,
          ...common,
        });
        toast.success(`Recorded ${(result.durationMs/1000).toFixed(1)}s to ${target.name}`);
      }
      setTransmissions(await listTransmissions({ limit: 100 }));
    } catch (e) {
      toast.error(formatApiError(e, "Failed to save transmission"));
    }
  }, [activeTarget, ptt, speech, settings.dispatcher_name, settings.transcription, user]);

  const selChannelObjs = useMemo(() => channels.filter(c => selChannels.includes(c.id)), [channels, selChannels]);
  const selContactObjs = useMemo(() => contacts.filter(c => selContacts.includes(c.id)), [contacts, selContacts]);

  const startBroadcast = () => {
    if (selChannels.length + selContacts.length === 0) return;
    startPTT({ type: "broadcast", name: `${selChannels.length + selContacts.length} targets`, channelIds: selChannels, contactIds: selContacts });
  };

  // Space bar transmits on the broadcast group (in select mode) or the last channel
  const lastChannelRef = useRef(null);
  useEffect(() => {
    const down = (e) => {
      if (e.code !== "Space" || e.repeat) return;
      const el = document.activeElement;
      if (el && ["INPUT", "TEXTAREA", "SELECT", "BUTTON"].includes(el.tagName)) return;
      if (activeTarget) return;
      if (selectMode && selChannels.length + selContacts.length > 0) { e.preventDefault(); startBroadcast(); return; }
      const target = lastChannelRef.current || channels[0];
      if (target) { e.preventDefault(); startPTT({ type: "channel", id: target.id, name: target.name }); }
    };
    const up = (e) => {
      if (e.code !== "Space") return;
      if (activeTarget && activeTarget.type !== "direct") { e.preventDefault(); stopPTT(); }
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  });

  // === Handlers ===
  const onChannelPTTStart = (ch) => { lastChannelRef.current = ch; startPTT({ type: "channel", id: ch.id, name: ch.name }); };
  const onDirectPTTStart = (ct) => startPTT({ type: "direct", id: ct.id, name: ct.name });
  const toggleIn = (setter) => (id) => setter(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectMode = () => { setSelectMode(m => !m); if (selectMode) { setSelChannels([]); setSelContacts([]); } };
  const selectAllChannels = () => setSelChannels(selChannels.length === channels.length ? [] : channels.map(c => c.id));

  const guard = (fn) => async (...args) => { try { await fn(...args); } catch (e) { toast.error(formatApiError(e)); } };

  const onAddChannel = guard(async (body) => { const ch = await createChannel(body); setChannels(p => [...p, ch]); toast.success(`Channel “${ch.name}” added`); });
  const onUpdateChannel = async (id, body) => {
    try { const ch = await updateChannel(id, body); setChannels(p => p.map(c => c.id === id ? ch : c)); toast.success(`Channel “${ch.name}” updated`); }
    catch (e) { toast.error(formatApiError(e)); throw e; }
  };
  const [toneActiveId, setToneActiveId] = useState(null);
  const onTone = async (ch) => {
    if (toneActiveId || activeTarget) return;
    setToneActiveId(ch.id);
    const type = ch.alert_tone || "two_tone", repeats = ch.tone_repeats || 1, volume = ch.tone_volume ?? 0.8;
    try {
      if (!muted) playTone(type, repeats, volume);
      const wav = await renderToneWav(type, repeats, volume);
      await createTransmission({ channel_id: ch.id, kind: "channel", sender_name: settings.dispatcher_name || user?.name || "Dispatcher", duration_ms: wav.durationMs, mime_type: wav.mimeType, audio_b64: wav.base64, tone: type, transcript: `Alert tone: ${TONES[type].label}` });
      toast(`${TONES[type].label} sent on ${ch.name}`, { duration: 1500 });
      setTransmissions(await listTransmissions({ limit: 100 }));
    } catch (e) { toast.error(formatApiError(e, "Failed to send tone")); }
    finally { setTimeout(() => setToneActiveId(null), Math.max(300, toneDurationMs(type, repeats))); }
  };
  const onDeleteChannel = guard(async (id) => { await deleteChannel(id); setChannels(p => p.filter(c => c.id !== id)); setSelChannels(p => p.filter(x => x !== id)); toast("Channel removed"); });
  const onAddContact = guard(async (body) => { const ct = await createContact(body); setContacts(p => [...p, ct]); toast.success(`${ct.name} added to roster`); });
  const onDeleteContact = guard(async (id) => { await deleteContact(id); setContacts(p => p.filter(c => c.id !== id)); setSelContacts(p => p.filter(x => x !== id)); });
  const onToggleOnline = guard(async (id) => { const ct = await toggleContactOnline(id); setContacts(p => p.map(c => c.id === id ? ct : c)); });
  const onAckAlert = guard(async (id) => { const a = await ackAlert(id); setAlerts(p => p.map(x => x.id === id ? a : x)); toast.success("Alert acknowledged"); });
  const onDismissAlert = guard(async (id) => { await deleteAlert(id); setAlerts(p => p.filter(x => x.id !== id)); });
  const onTriggerSOS = guard(async () => {
    const online = contacts.filter(c => c.online);
    if (online.length === 0) return;
    const random = online[Math.floor(Math.random() * online.length)];
    const a = await createAlert({ contact_id: random.id, message: `Test SOS from ${random.name}`, severity: "high" });
    setAlerts(p => [a, ...p]);
    toast.warning(`SOS triggered by ${random.name}`);
  });
  const onAddPatch = guard(async (body) => { const p = await createPatch(body); setPatches(x => [...x, p]); toast.success(`Patch “${p.name}” is live`); });
  const onTogglePatch = guard(async (id) => { const p = await togglePatch(id); setPatches(x => x.map(y => y.id === id ? p : y)); toast(p.active ? `Patch “${p.name}” resumed` : `Patch “${p.name}” paused`); });
  const onDeletePatch = guard(async (id) => { await deletePatch(id); setPatches(x => x.filter(y => y.id !== id)); toast("Patch removed"); });
  const onTalkgroupPTTStart = (tg) => startPTT({ type: "talkgroup", id: tg.id, name: `talkgroup ${tg.name}` });
  const onCreateTalkgroup = async (body) => {
    try { const tg = await createTalkgroup(body); setTalkgroups(x => [...x, tg]); toast.success(`Talkgroup “${tg.name}” ready`); }
    catch (e) { toast.error(formatApiError(e)); throw e; }
  };
  const onDeleteTalkgroup = guard(async (id) => { await deleteTalkgroup(id); setTalkgroups(x => x.filter(t => t.id !== id)); toast("Talkgroup torn down"); });
  const onDropOnTalkgroup = guard(async (tgId, contactId) => {
    const tg = await addTalkgroupMembers(tgId, [contactId]);
    setTalkgroups(x => x.map(t => t.id === tgId ? tg : t));
    const c = contacts.find(c => c.id === contactId);
    toast.success(`${c?.name || "Unit"} added to ${tg.name}`);
  });
  const onRemoveMember = guard(async (tgId, contactId) => { const tg = await removeTalkgroupMember(tgId, contactId); setTalkgroups(x => x.map(t => t.id === tgId ? tg : t)); });
  const hiddenPanes = settings.hidden_panes || [];
  const onTogglePane = (id) => {
    const next = hiddenPanes.includes(id) ? hiddenPanes.filter(x => x !== id) : [...hiddenPanes, id];
    setSettings(s => ({ ...s, hidden_panes: next }));
    saveSettings({ ...settings, hidden_panes: next }).catch(() => {});
  };
  const onSaveSettings = async (body) => {
    try { const s = await saveSettings(body); setSettings(s); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiError(e)); throw e; }
  };

  // Layout persistence (per-user, debounced)
  const layout = useMemo(() => {
    const saved = settings.layout;
    if (!Array.isArray(saved) || saved.length === 0) return DEFAULT_LAYOUT;
    const byId = Object.fromEntries(saved.map(l => [l.i, l]));
    return DEFAULT_LAYOUT.map(d => ({ ...d, ...(byId[d.i] ? { x: byId[d.i].x, y: byId[d.i].y, w: byId[d.i].w, h: byId[d.i].h } : {}) }));
  }, [settings.layout]);
  const saveTimer = useRef(null);
  const onLayoutChange = (next) => {
    if (layoutLocked) return;
    const compact = next.map(({ i, x, y, w, h }) => ({ i, x, y, w, h }));
    const ids = new Set(compact.map(l => l.i));
    layout.filter(l => !ids.has(l.i)).forEach(({ i, x, y, w, h }) => compact.push({ i, x, y, w, h }));
    setSettings(s => ({ ...s, layout: compact }));
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveSettings({ ...settings, layout: compact }).catch(() => {}), 800);
  };
  const onResetLayout = () => { setSettings(s => ({ ...s, layout: null, hidden_panes: [] })); saveSettings({ ...settings, layout: null, hidden_panes: [] }).then(() => toast("Layout reset")).catch(() => {}); };

  const patchesByChannel = useMemo(() => {
    const m = {};
    patches.filter(p => p.active).forEach(p => p.channel_ids.forEach(id => { (m[id] ||= []).push(p); }));
    return m;
  }, [patches]);

  const txStatus = isTransmitting ? "transmitting" : "idle";
  const activeChannelId = activeTarget?.type === "channel" ? activeTarget.id : null;
  const activeDirectId = activeTarget?.type === "direct" ? activeTarget.id : null;
  const activeTalkgroupId = activeTarget?.type === "talkgroup" ? activeTarget.id : null;
  const broadcastStatus = activeTarget?.type === "broadcast" ? txStatus : "idle";

  const panes = {
    roster: <RosterSidebar contacts={contacts} activeDirectId={activeDirectId} txStatus={txStatus} onPTTStart={onDirectPTTStart} onPTTEnd={stopPTT} onAdd={onAddContact} onDelete={onDeleteContact} onToggleOnline={onToggleOnline} selectMode={selectMode} selectedIds={selContacts} onToggleSelect={toggleIn(setSelContacts)} onCreateTalkgroup={onCreateTalkgroup} />,
    channels: <ChannelGrid channels={channels} activeChannelId={activeChannelId} txStatus={txStatus} onPTTStart={onChannelPTTStart} onPTTEnd={stopPTT} onAdd={onAddChannel} onDelete={onDeleteChannel} onUpdate={onUpdateChannel} onTone={onTone} toneActiveId={toneActiveId} selectMode={selectMode} onToggleSelectMode={toggleSelectMode} selectedIds={selChannels} onToggleSelect={toggleIn(setSelChannels)} onSelectAll={selectAllChannels} patchesByChannel={patchesByChannel}
      patches={patches} onAddPatch={onAddPatch} onTogglePatch={onTogglePatch} onDeletePatch={onDeletePatch}
      talkgroups={talkgroups} contacts={contacts} activeTalkgroupId={activeTalkgroupId} onTalkgroupPTTStart={onTalkgroupPTTStart} onDeleteTalkgroup={onDeleteTalkgroup} onDropOnTalkgroup={onDropOnTalkgroup} onRemoveMember={onRemoveMember} />,
    map: <LiveMap contacts={contacts} alerts={alerts} settings={settings} activeDirectId={activeDirectId} txStatus={txStatus} onPTTStart={onDirectPTTStart} onPTTEnd={stopPTT} selected={mapSelected} setSelected={setMapSelected} />,
    alerts: <EmergencyAlerts alerts={alerts} onAck={onAckAlert} onDismiss={onDismissAlert} onTriggerSOS={onTriggerSOS} canTrigger={contacts.some(c => c.online)} />,
    logger: <AudioLogger transmissions={transmissions} timezone={settings.timezone} speakerDeviceId={settings.speaker_device_id} muted={muted} onDeleted={() => listTransmissions({ limit: 100 }).then(setTransmissions)} />,
  };

  return (
    <div data-testid={IDS.app} className="App min-h-screen grid-bg">
      <DispatchHeader
        muted={muted} onMuteToggle={() => setMuted(m => !m)} timezone={settings.timezone}
        settings={settings} onSaveSettings={onSaveSettings} onResetLayout={onResetLayout}
        layoutLocked={layoutLocked} onToggleLayoutLock={() => setLayoutLocked(l => !l)} speechSupported={speech.supported}
        hiddenPanes={hiddenPanes} onTogglePane={onTogglePane}
      />
      <DashboardGrid layout={layout} onLayoutChange={onLayoutChange} locked={layoutLocked} panes={panes} hidden={hiddenPanes} onHide={onTogglePane} />
      <div className={`text-center text-[11px] text-slate-400 pb-4 ${selectMode ? "pb-28" : ""}`}>
        Hold <kbd className="font-mono px-1.5 py-0.5 border border-slate-200 rounded bg-white">Space</kbd> to transmit on the {selectMode ? "broadcast group" : "last used channel"}
        {!layoutLocked && " · Layout editing: drag pane headers, resize from corners"}
      </div>

      {selectMode && (
        <BroadcastBar
          channels={selChannelObjs} contacts={selContactObjs} status={broadcastStatus}
          onPTTStart={startBroadcast} onPTTEnd={stopPTT}
          onClear={() => { setSelChannels([]); setSelContacts([]); }} onExit={toggleSelectMode}
        />
      )}
    </div>
  );
}

function Gate() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid-bg grid place-items-center text-sm text-slate-500 font-mono">Loading console…</div>;
  if (!user) return <LoginPage />;
  return <Console />;
}

export default function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <Gate />
    </AuthProvider>
  );
}
